import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Chip
} from '@mui/material';
import {
  Business,
  AdminPanelSettings,
  SupervisorAccount,
  Category,
  Transform,
  Code,
  Assignment,
  SmartToy,
  Monitor
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const adminMenuItems = [
  // 고객사 온톨로지 관리 섹션 (첫 번째)
  {
    divider: true,
    sectionTitle: '고객사 온톨로지 관리'
  },
  {
    text: '파일 온톨로지',
    icon: <Category />,
    isSubheader: true  // 하위 그룹 헤더로 표시
  },
  {
    text: '파일 온톨로지 관리',
    icon: <Transform />,
    path: '/admin/ontology',
    isCustomerFeature: true,
    indent: true  // 들여쓰기 표시
  },
  {
    text: '프롬프트 관리',
    icon: <Assignment />,
    isSubheader: true  // 하위 그룹 헤더로 표시
  },
  {
    text: 'AI 변환 프롬프트',
    icon: <Code />,
    path: '/admin/prompt-management',
    isCustomerFeature: true,
    indent: true  // 들여쓰기 표시
  },
  // 데이터변환 섹션 (두 번째)
  {
    divider: true,
    sectionTitle: '데이터변환'
  },
  {
    text: 'Hyperlounge Converter',
    icon: <Code />,
    path: '/admin/hyperlounge-converter',
    isCustomerFeature: true,
    indent: true  // 들여쓰기 표시
  },
  {
    text: '에이전트 모니터링',
    icon: <Monitor />,
    path: '/admin/agent-monitor',
    indent: true
  },
  // 설정 섹션 (세 번째)
  {
    divider: true,
    sectionTitle: '설정'
  },
  {
    text: '관리자 계정',
    icon: <AdminPanelSettings />,
    path: '/admin/admin-management',
    indent: true
  },
  {
    text: '테넌트 관리',
    icon: <Business />,
    path: '/admin/customers',
    indent: true
  }
];

const AdminSidebar = ({ onClose, isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  const isSelected = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <Box>
      {/* Logo/Title */}
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AdminPanelSettings color="error" />
          <Typography variant="h6" noWrap component="div" color="error">
            Ontology Manager
          </Typography>
        </Box>
      </Toolbar>
      
      <Divider />

      {/* Admin Navigation Menu */}
      <List>
        {adminMenuItems.map((item, index) => {
          // Handle section dividers
          if (item.divider) {
            return (
              <React.Fragment key={`divider-${index}`}>
                <Divider sx={{ my: 1 }} />
                {item.sectionTitle && (
                  <ListItem>
                    <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                      {item.sectionTitle}
                    </Typography>
                  </ListItem>
                )}
              </React.Fragment>
            );
          }
          
          // Handle subheader items (like "파일 온톨로지")
          if (item.isSubheader) {
            return (
              <ListItem key={item.text} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontWeight: 'medium',
                    fontSize: '0.95rem',
                    color: 'text.secondary'
                  }}
                />
              </ListItem>
            );
          }
          
          // Handle regular menu items
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isSelected(item.path)}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  pl: item.indent ? 6 : 2,  // 들여쓰기 적용
                  '&.Mui-selected': {
                    backgroundColor: 'error.main',
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '&:hover': {
                      backgroundColor: 'error.dark',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontSize: item.indent ? '0.9rem' : '1rem'
                  }}
                />
                {item.badge && (
                  <Chip
                    label={item.badge}
                    size="small"
                    color={item.badge === 'Admin' ? 'error' : 'primary'}
                    variant={isSelected(item.path) ? 'filled' : 'outlined'}
                    sx={{ 
                      fontSize: '0.75rem',
                      height: 20,
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* Admin Info */}
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          p: 1,
          bgcolor: 'error.main',
          color: 'white',
          borderRadius: 1
        }}>
          <SupervisorAccount fontSize="small" />
          <Typography variant="caption">
            관리자 모드
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminSidebar;